import React, { createContext, useContext } from 'react';

/** Action functions exposed by the page (e.g. open modal, run projection). Plan/Region may pass {}. */
export type PageDataActions = Record<string, (...args: unknown[]) => void | Promise<void>>;

export interface PageDataContextValue {
  /** Page-specific data (timeSeriesData, fisheryStats, insights, etc.) */
  data: Record<string, unknown>;
  loading: boolean;
  error: string | null;
  /** Optional submit/recalculate for pages with form-driven data (e.g. Stock) */
  submit?: () => Promise<void>;
  /** Optional actions (e.g. openExVesselModal, runGroundfishProjection). Stock passes these; Plan/Region may pass {}. */
  actions?: PageDataActions;
}

const PageDataContext = createContext<PageDataContextValue | null>(null);

export interface PageDataProviderProps {
  children: React.ReactNode;
  value: PageDataContextValue;
}

export function PageDataProvider({ children, value }: PageDataProviderProps): React.ReactElement {
  return (
    <PageDataContext.Provider value={value}>
      {children}
    </PageDataContext.Provider>
  );
}

export function usePageDataContext(): PageDataContextValue {
  const context = useContext(PageDataContext);
  if (!context) {
    throw new Error('usePageDataContext must be used within a PageDataProvider');
  }
  return context;
}

/**
 * Optional: use PageDataContext if it exists, but don't throw when outside.
 * Useful for registry components that can work with either context or injections.
 */
export function usePageDataContextOptional(): PageDataContextValue | null {
  return useContext(PageDataContext);
}
